<template>
  <page-container>
    <div class="flex justify-between items-center mb4">
      <heading size="large" :spacing-bottom="false">
        <span v-if="isEditMode && !isCopyMode">{{
          $t("nav.editResource")
        }}</span>
        <span v-if="isCopyMode">Copy resource</span>
        <span v-if="!isEditMode">{{ $t("nav.addResource") }}</span>
      </heading>

      <b-field>
        <b-dropdown
          animation="pop"
          v-model="resource.team_id"
          position="is-bottom-left"
        >
          <b-button
            slot="trigger"
            class="is-fullwidth has-flexed-content has-equal-padding"
            outlined
          >
            <span class="mr2">Team: </span>
            <span v-if="resource.team_id" class="mra">
              {{ teams[resource.team_id].name }}
            </span>
            <span v-else class="o-40">None</span>
            <nice-icon icon="chevron-down" class="ml2"></nice-icon>
          </b-button>

          <b-dropdown-item :value="null">
            <span>None</span>
          </b-dropdown-item>
          <b-dropdown-item
            v-for="team in teams"
            :key="team.id"
            :value="team.id"
          >
            <span>{{ team.name }}</span>
          </b-dropdown-item>
        </b-dropdown>
      </b-field>
    </div>
    <fieldset
      :disabled="$wait.is('creating resource') || $wait.is('loading resource')"
    >
      <b-field label="URL">
        <b-input
          @blur="loadTitle"
          v-model="resource.url"
          autofocus
          ref="urlInput"
        ></b-input>
      </b-field>
      <b-field label="Name" :message="$t('messages.titleNotice')" class="mb4">
        <b-input
          v-model="resource.name"
          ref="nameInput"
          :disabled="$wait.is('loading resource title')"
          :loading="$wait.is('loading resource title')"
        ></b-input>
      </b-field>
      <b-field :label="$t('fields.comment')" class="mb4">
        <b-input v-model="resource.comment" type="textarea"></b-input>
      </b-field>

      <b-field
        v-if="isCopyMode"
        label="Tags of other user"
        class="mb3"
        message="These tags will not get copied!"
      >
        <advanced-tag-list
          :use="resource.tags"
          :show-all="true"
          :can-load-more="false"
          :show-tag-count="false"
          :filterable="false"
          :read-only="true"
          no-context-menu
          :context-props="{ positionAbsolute: isInOverlay }"
          :context="currentContext"
        ></advanced-tag-list>
      </b-field>

      <div class="flex justify-between mb2 mt4">
        <div>
          Your Tags
          <b-tooltip
            type="is-dark"
            position="is-right"
            label="Use the keys tab, arrow-left and arrow-right to navigate through the suggestions as you type. Hit enter to add the currently selected tag. This should also work on your mobile device."
            multilined
          >
            <nice-icon
              icon="question-circle"
              spacing-right
              size="sm"
            ></nice-icon>
          </b-tooltip>
        </div>
        <b-dropdown
          aria-role="list"
          position="is-bottom-left"
          class="recent-dropdown"
          ref="recentDropdown"
          animation="pop"
          v-if="!recentTagCombinationsEmpty"
        >
          <p
            slot="trigger"
            class="has-pointer normal"
            style="user-select:none;"
          >
            <!-- <nice-icon icon="history" spacing-right></nice-icon> -->
            <span>{{ $t("buttons.recentlyUsed") }}</span>
            <nice-icon type="fas" icon="caret-down" class="ml2"></nice-icon>
            <!-- <nice-icon icon="caret-down"></nice-icon> -->
          </p>

          <div v-if="recentTagCombinations && recentTagCombinations.length > 0">
            <b-dropdown-item
              paddingless
              custom
              :focusable="false"
              v-for="(resource, index) in recentTagCombinations"
              :key="`comb-${index}`"
              class="recent-dropdown-item"
            >
              <p class="mb2 f5 flex items-center justify-between">
                <span>{{ resource.name | truncate }}</span>
                <b-button
                  type="is-primary"
                  size="is-small"
                  @click="applyTags(resource.tags, $event)"
                  >Apply</b-button
                >
              </p>
              <div class="flex flex-wrap recent-tags-item">
                <tag-item
                  v-for="tag in resource.tags"
                  :key="`tag-item-${tag.id}`"
                  :tag="tag"
                  is-in-dropdown
                  class="tag"
                ></tag-item>
              </div>
              <hr
                v-if="index < recentTagCombinations.length - 1"
                class="recent-tags-divider"
              />
            </b-dropdown-item>
          </div>

          <div style="padding: 20px 0 10px 0;" v-if="!recentShowMore">
            <b-button
              @click.prevent.stop="recentShowMore = true"
              size="is-small"
              style="display:block;margin:0 auto;"
              >Show more</b-button
            >
          </div>
        </b-dropdown>
      </div>
      <b-field>
        <advanced-tag-list
          :use="tags"
          :show-all="true"
          :can-load-more="false"
          :show-tag-count="false"
          :filterable="false"
          closable
          @close="onTagClose"
          :tag-props="{
            closable: true
          }"
          :context-options="{
            positionAbsolute: isInOverlay,
            userIsOwner: true,
            preventRemove: true,
            preventSearchAdd: true
          }"
          :context="currentContext"
        ></advanced-tag-list>
      </b-field>
      <advanced-tag-list
        :use="tagsSortedByResourceCountAndText"
        :exclude="tagIds"
        @tagClicked="addTag"
        @beforeTagCreate="onBeforeTagCreate"
        @errorTagCreate="onErrorTagCreate"
        :show-all="false"
        class="mb3 add-tags"
        :chunk="50"
        :is-in-dropdown="false"
        :limit-height="true"
        :can-load-more="true"
        :context-options="{
          positionAbsolute: isInOverlay,
          userIsOwner: true,
          preventRemove: true,
          preventSearchAdd: true
        }"
        :tag-props="{ canGrow: true }"
        ref="tagInput"
        :context="currentContext"
      >
        <template slot="menu" slot-scope="{ child }">
          <a class="dropdown-item pointer" @click="addTag(child.data)">
            <nice-icon icon="check" spacing-right></nice-icon>
            <span>Use tag</span>
          </a>
        </template>
      </advanced-tag-list>

      <b-field>
        <b-switch v-model="resource.is_public">{{
          $t("fields.resourcePublic")
        }}</b-switch>
      </b-field>

      <b-field>
        <b-switch v-model="resource.is_pinned"
          >Resource is pinned to the top</b-switch
        >
      </b-field>
    </fieldset>

    <div class="mt4">
      <b-button
        type="is-primary"
        @click="create()"
        :disabled="isSaveButtonDisabled"
        :loading="$wait.is('creating resource')"
        >{{ $t("buttons.save") }}</b-button
      >
      <b-button
        @click="cancel()"
        :disabled="$wait.is('creating resource')"
        class="ml3"
        >{{ $t("buttons.cancel") }}</b-button
      >
    </div>
  </page-container>
</template>

<script>
import AdvancedTagList from "@/shared/components/tag/AdvancedList";
import TagItem from "@/shared/components/tag/Item.vue";

function initalState() {
  return {
    resource: {
      name: "",
      url: "",
      comment: "",
      is_public: false,
      is_pinned: false,
      tags: [],
      team_id: null
    },
    tags: [],
    urlBefore: null,
    recentShowMore: false
  };
}

export default {
  name: "add-resource",

  props: {
    resourceData: {
      type: Object
    },
    isInOverlay: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      resource: initalState().resource,
      tags: initalState().tags,
      urlBefore: initalState().urlBefore,
      isActivated: initalState().isActivated,
      recentShowMore: initalState().recentShowMore
    };
  },

  watch: {
    "resource.team_id": {
      handler(val) {
        if (val) {
          this.$store.commit("SET_CURRENT_CONTEXT", "team");
          if (this.$store.state.team.currentId != val) {
            this.$store.commit("team/SET_CURRENT_ID", val);
            this.$store.dispatch("team/fetchTags");
          }
        } else {
          this.$store.commit("SET_CURRENT_CONTEXT", "own");
        }
      }
    },

    "$route.params.id": function(val) {
      if (!val) {
        this.setResource(initalState().resource);
      }
    },

    resourceData: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val && !this.$utils.isEmpty(val)) {
          if (val.url === this.resource.url) {
            val.name = this.resource.name;
          } else {
            this.$set(this, "tags", initalState().tags);
          }
          this.$set(this, "resource", this.$utils.cloneDeep(val));
          this.addDomainTag();

          if (!val.name) {
            this.loadTitle();
          }

          this.loadResourceForURL();
        } else {
          this.setResource(initalState().resource);
        }
      }
    },

    initAndContext: {
      immediate: true,
      async handler(val) {
        if (val) {
          this.reset();

          let preventTitleLoad = false;

          const teamId = this.$route.query.t;
          if (
            !this.isEditMode &&
            !!teamId &&
            this.$store.getters["team/teams"][teamId]
          ) {
            this.resource.team_id = teamId;
            this.$store.commit("team/SET_CURRENT_ID", teamId);
            if (this.$store.getters["team/storedTagsTeamId"] !== teamId) {
              this.$store.commit("team/CLEAR_TAGS");
              this.$store.dispatch("team/fetchTags");
            }
            this.$store.commit("SET_CURRENT_CONTEXT", "team");
          }

          if (this.isEditMode) {
            const id = this.$route.params.id;
            let resource = null;

            if (!this.isCopyMode) {
              resource = this.$store.getters[
                `${this.currentContext}/resources`
              ][id];
            }

            if (!resource) {
              this.$wait.start("loading resource");
              resource = await this.$store.dispatch("fetchResource", id);
              this.$wait.end("loading resource");

              if (this.isCopyMode) {
                resource.is_public = false;
                resource.is_pinned = false;
                resource.id = null;
              }
            }

            if (!resource) this.$router.replace({ name: "error" });

            this.urlBefore = resource.url;

            this.setResource(this.$utils.cloneDeep(resource), !this.isCopyMode);
          } else {
            const query = this.$route.query;
            if (query.url || query.text) {
              let url = null;
              if (this.$utils.isUrl(query.text)) {
                url = query.text;
              } else {
                url = query.url;
                this.resource.comment = this.$route.query.text;
                preventTitleLoad = true;
              }
              this.resource.name = this.$route.query.name;
              this.resource.url = url;
              this.loadTitle();
            }
          }

          if (!preventTitleLoad && !this.isInOverlay) {
            try {
              const permission = await navigator.permissions.query({
                name: "clipboard-read"
              });

              if (
                permission.state == "granted" ||
                permission.state == "prompt"
              ) {
                navigator.clipboard.readText().then(clipText => {
                  if (
                    !this.isEditMode &&
                    clipText &&
                    this.$utils.isUrl(clipText) &&
                    clipText.length < 200
                  ) {
                    this.resource.url = clipText;
                    this.loadTitle();
                    this.$nextTick(() => {
                      this.$refs.urlInput.checkHtml5Validity();
                    });
                  }
                });
              }
            } catch (error) {}
          }
        }
      }
    }
  },

  methods: {
    onTagClose(tag) {
      this.tags.splice(this.tags.findIndex(t => t.id == tag.id), 1);
    },

    addTag(tag) {
      const index = this.tags.findIndex(
        t => t.isLoading && t.text === tag.text
      );
      if (index > -1) {
        this.$set(this.tags, index, tag);
      } else {
        this.tags.push(tag);
      }
    },
    onErrorTagCreate(tag) {
      const index = this.tags.findIndex(
        t => t.isLoading && t.text === tag.text
      );
      if (index > -1) {
        this.$delete(this.tags, index);
      }
    },
    onBeforeTagCreate(tag) {
      this.tags.push(tag);
    },

    cancel() {
      if (this.isInOverlay) {
        this.$emit("cancel");
      } else {
        this.navigateBackOrDashboard();
      }
    },

    reset() {
      for (const key in initalState()) {
        this.$set(this, key, initalState()[key]);
      }
    },

    async create() {
      if (this.resource.name.length < 1 || this.resource.url.length < 1) {
        this.$buefy.toast.open({
          message: `Name and URL can not be empty!`,
          type: "is-danger"
        });
        return false;
      }

      let success = true;

      const addToDashboard = !this.resource.team_id;

      const isNewUser = this.$store.getters["own/defaultResourcesEmpty"];

      if (!this.isEditMode || this.isCopyMode) {
        success = await this.$store.dispatch(
          `${this.currentContext}/createResource`,
          {
            resource: this.resource,
            tags: this.tags,
            addToDashboard
          }
        );
      } else {
        success = await this.$store.dispatch(
          `${this.currentContext}/updateResource`,
          {
            resource: this.resource,
            tags: this.tags,
            addToDashboard
          }
        );
      }

      if (success) {
        if (this.isInOverlay) {
          this.$emit("saved");
        } else {
          if (isNewUser) {
            this.$router.push({ name: "own.resources" });
          } else {
            this.navigateBackOrDashboard(true);
          }
        }
      }

      setTimeout(() => {
        this.reset();
      }, 250);
    },

    async loadResourceForURL() {
      const resourceWithUrl = await this.$store.dispatch(
        "fetchResourceByURL",
        this.resource.url
      );
      if (resourceWithUrl) {
        this.$buefy.dialog.confirm({
          animation: "pop-modal",
          title: resourceWithUrl.name,
          message:
            "A resource with this url already exits. Want to edit it instead?",
          confirmText: "Edit existing",
          cancelText: "Create new",
          onConfirm: async () => {
            this.$router.replace({
              name: "resource",
              params: { id: resourceWithUrl.id }
            });
            this.setResource(resourceWithUrl);
          }
        });
      }
    },

    async loadTitle() {
      if (
        this.resource.url === this.urlBefore ||
        this.$utils.isEmpty(this.resource.url) ||
        !this.$utils.isUrl(this.resource.url)
      ) {
        return false;
      }

      this.loadResourceForURL();

      try {
        this.$wait.start("loading resource title");
        this.urlBefore = this.resource.url;
        const resp = await this.axios.post("utils/getTitleByUrl", {
          url: this.resource.url
        });
        this.resource.name = resp.data.data;

        if (this.$settings.tags.autoCreateForDomain) {
          this.addDomainTag();
        }
      } catch (e) {
        this.$buefy.toast.open({
          message: "Unable to load title. Please enter manually.",
          duration: 5000
        });
      }
      this.$wait.end("loading resource title");
      // this.$nextTick(() => {
      //   this.$refs.nameInput.checkHtml5Validity();
      // });
    },

    setResource(resource, setTags = true) {
      this.$set(this, "resource", resource);

      if (setTags) {
        this.$set(this, "tags", resource.tags);
      }
    },

    getDomainName() {
      const url = new URL(this.resource.url);
      const hostnameSplitted = url.hostname.split(".");
      const index =
        hostnameSplitted.length > 1 ? hostnameSplitted.length - 2 : 0;
      const domain = hostnameSplitted[index];
      return domain;
    },

    async addDomainTag() {
      const domain = this.getDomainName();
      let tag = this.$store.getters[`${this.currentContext}/tagsArray`].find(
        t => t.text === domain
      );
      if (!tag) {
        tag = await this.$store.dispatch(`${this.currentContext}/createTag`, {
          text: domain
        });
      }
      if (!this.tags.find(t => t.id === tag.id)) {
        this.tags.push(tag);
      }
      // this.$set(this, "tags", [tag]);
    },

    applyTags(tags) {
      for (const tag of tags) {
        if (!this.tags.find(t => t.id === tag.id)) this.tags.push(tag);
      }
      this.$refs.recentDropdown.toggle();
    }
  },

  computed: {
    tagIds() {
      return this.tags.map(t => t.id);
    },
    tagsArray() {
      return this.$store.getters[`${this.currentContext}/tagsArray`];
    },
    tagsSortedByResourceCountAndText() {
      return this.$store.getters[
        `${this.currentContext}/tagsSortedByResourceCountAndText`
      ];
    },

    isEditMode() {
      return !!this.$route.params.id;
    },
    isCopyMode() {
      return this.$route.query.mode === "copy";
    },

    activatedAndInitComplete() {
      return this.isActivated && this.isInitCompleted;
    },

    isInitCompleted() {
      return !!this.$store.state.initCompleted;
    },

    recentTagCombinations() {
      let count = 3;
      if (this.recentShowMore) count = 10;
      const recentNewResources = this.$store.getters[
        `${this.currentContext}/default/resources`
      ].slice(0, count);
      return recentNewResources;
    },

    recentTagCombinationsEmpty() {
      return this.recentTagCombinations.length < 1;
    },

    isSaveButtonDisabled() {
      return (
        !this.resource.url ||
        !this.$utils.isUrl(this.resource.url) ||
        !this.resource.name
      );
    },

    teams() {
      return this.$store.getters["team/teams"];
    }
  },

  created() {
    this.isActivated = true;
    // if (!this.isInOverlay) {
    //   window.onbeforeunload = function() {
    //     return "Are you sure you want to leave?";
    //   };
    // }
  },
  beforeDestroy() {
    this.isActivated = false;
    this.$store.commit("team/SET_CURRENT_ID", null);
    this.$store.commit("SET_CURRENT_CONTEXT", null);
  },

  components: {
    TagItem,
    AdvancedTagList
  }
};
</script>

<style lang="scss" scoped>
.is-main {
  position: fixed;
  left: 20px;
  bottom: 20px;
  z-index: 10;
  border-radius: 50%;
  height: 55px;
  width: 55px;

  @include tablet() {
    left: 40px;
    bottom: 40px;
  }
}

.resource-tags {
  min-height: 40px;
  margin-bottom: 0.75rem !important;
}

.title {
  color: $text;
  font-size: 30px;
}

.add-tags {
  // padding: 10px;
  // border-radius: $radius;
  // background: $dark;
  margin-top: 20px;
  margin-bottom: 40px;
}

.recent-tags {
  &-item {
    margin-bottom: -5px;

    .tag {
      &:not(:last-of-type) {
        margin-right: 8px;
      }
    }
  }

  &-divider {
    height: 1px;
    background-color: $light_10;
    margin-bottom: 0;
    margin-top: 15px;
  }
}

.recent-dropdown-item {
  padding: 0.75rem !important;

  &:not(:last-of-type) {
    padding-bottom: 0 !important;
  }
}

@include tablet() {
  .recent-dropdown-item {
    width: 400px;
  }
}
</style>

<style lang="scss">
.recent-dropdown {
  .dropdown-content {
    max-height: 400px;
    overflow-y: auto;
  }
}
</style>
